import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Plus } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { peopleService } from '../../services/peopleService';
import api from '../../services/api';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import { useColumnFilters, FilterPopover } from '../../components/gridFilters';
import PickerPopover from '../../components/PickerPopover';
import SearchSelect from '../../components/SearchSelect';

// =============================================================================
// GL Mapping — maps Site + Job Title → GL account + cost centre, per budget
// version (bud_people_gl_map). This is what lets people costs (Structures) be
// rolled up into a GL view. One mapping per (site, job title); the GL account
// and cost centre are editable in-grid via click-pickers.
// =============================================================================


const newId = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0; const v = c === 'x' ? r : (r & 0x3) | 0x8; return v.toString(16);
      }));

const BLANK_ENTRY = { site_code: '', job_title: '', account_code: '', cost_center_code: '' };

const GlMapping = () => {
  const { selectedVersion } = useAppContext();
  const [mappings, setMappings] = useState([]);   // [{ map_id, site_code, job_title, account_code, cost_center_code, isNew }]
  const [accounts, setAccounts] = useState([]);   // [{ code, description }]
  const [costCenters, setCostCenters] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [sites, setSites] = useState([]);
  const [dirty, setDirty] = useState({});
  const [picker, setPicker] = useState(null);     // { id, field: 'account'|'cc', x, y }
  const [filterUI, setFilterUI] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [entry, setEntry] = useState(BLANK_ENTRY);

  // Option lists for the inline add line's type-to-search pickers. `code` is
  // the stored value; `name` is what a human searches for.
  const siteOptions = useMemo(() => sites.map((s) => ({ code: s.code, name: s.name })), [sites]);
  const titleOptions = useMemo(() => jobTitles.map((j) => ({ code: j.title })), [jobTitles]);
  const accountOptions = useMemo(() => accounts.map((a) => ({ code: a.code, name: a.description })), [accounts]);
  const ccOptions = useMemo(() => costCenters.map((c) => ({ code: c.code, name: c.description })), [costCenters]);

  const ready = !!selectedVersion;

  const siteName = useCallback((code) => sites.find((s) => s.code === code)?.name || code || '—', [sites]);
  const accLabel = useCallback((code) => {
    const a = accounts.find((x) => x.code === code);
    return a ? `${a.code} - ${a.description}` : (code || '—');
  }, [accounts]);
  const ccLabel = useCallback((code) => {
    if (!code) return '—';
    const c = costCenters.find((x) => x.code === code);
    return c ? `${c.code} - ${c.description}` : code;
  }, [costCenters]);

  const loadData = useCallback(async () => {
    if (!ready) { setMappings([]); return; }
    setIsLoading(true); setError(null);
    try {
      const [mapRes, jtRes] = await Promise.all([
        peopleService.getGlMap(),
        peopleService.getJobTitles(),
      ]);
      if (!mapRes.success) throw new Error(mapRes.message);
      setMappings((mapRes.data.mappings || []).map((m) => ({ ...m, isNew: false })));
      setAccounts(mapRes.data.accounts || []);
      setCostCenters(mapRes.data.costCenters || []);
      setJobTitles(jtRes?.success ? (jtRes.data.jobTitles || []) : []);
      setDirty({});
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load GL mapping.');
      setMappings([]);
    } finally { setIsLoading(false); }
  }, [ready]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    let cancelled = false;
    if (!ready) return;
    api.get('/sites/load-all')
      .then((r) => { if (!cancelled) setSites(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ready]);

  // ---- Filters ----------------------------------------------------------------
  const accessors = useMemo(() => ({
    site: (m) => siteName(m.site_code),
    job_title: (m) => m.job_title,
    account: (m) => accLabel(m.account_code),
    cost_center: (m) => ccLabel(m.cost_center_code),
  }), [siteName, accLabel, ccLabel]);
  const { filters, setFilter, clearAll, filtered, activeIds } = useColumnFilters(mappings, accessors);

  const mappingsRef = useRef([]);
  mappingsRef.current = mappings;
  const filteredRef = useRef([]);
  filteredRef.current = filtered;

  // ---- Grid --------------------------------------------------------------------
  const gcols = useMemo(() => {
    const dot = (id, title) => (activeIds.includes(id) ? `${title} ●` : title);
    return [
      { id: 'site', title: dot('site', 'Site'), width: 140, grow: 0.5, hasMenu: true },
      { id: 'job_title', title: dot('job_title', 'Job Title'), width: 180, grow: 0.7, hasMenu: true },
      { id: 'account', title: dot('account', 'GL Account'), width: 240, grow: 1, hasMenu: true },
      { id: 'cost_center', title: dot('cost_center', 'Cost Centre'), width: 200, grow: 1, hasMenu: true },
      { id: 'del', title: '', width: 36 },
    ];
  }, [activeIds]);
  const colIds = useMemo(() => gcols.map((c) => c.id), [gcols]);

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];
    const m = filtered[row];
    if (!m) return gcell.text('', { readonly: true });
    switch (id) {
      case 'site': return gcell.text(siteName(m.site_code), { readonly: true });
      case 'job_title': return gcell.text(m.job_title, { readonly: true });
      case 'account': return gcell.text(accLabel(m.account_code), { readonly: true });
      case 'cost_center': return gcell.text(ccLabel(m.cost_center_code), { readonly: true });
      case 'del': return gcell.text('✕', { readonly: true, theme: { textDark: '#94a3b8' } });
      default: return gcell.text('', { readonly: true });
    }
  }, [colIds, filtered, siteName, accLabel, ccLabel]);

  const onDelete = useCallback(async (id) => {
    const m = mappingsRef.current.find((x) => x.map_id === id);
    if (m?.isNew) {
      setMappings((prev) => prev.filter((x) => x.map_id !== id));
      setDirty((prev) => { const d = { ...prev }; delete d[id]; return d; });
      return;
    }
    if (!window.confirm(`Remove mapping for ${m?.job_title || ''} @ ${siteName(m?.site_code)}?`)) return;
    setError(null);
    try { await peopleService.deleteGlMap(id); await loadData(); }
    catch (e) { setError(e.response?.data?.message || 'Failed to delete mapping.'); }
  }, [loadData, siteName]);

  const onCellClicked = useCallback(([col, row], event) => {
    const m = filteredRef.current[row];
    if (!m) return;
    const id = colIds[col];
    const b = event?.bounds;
    const pos = { x: b ? b.x : 200, y: b ? b.y + b.height : 200 };
    if (id === 'account') setPicker({ id: m.map_id, field: 'account', ...pos });
    else if (id === 'cost_center') setPicker({ id: m.map_id, field: 'cc', ...pos });
    else if (id === 'del') onDelete(m.map_id);
  }, [colIds, onDelete]);

  const applyPick = useCallback((mapId, field, code) => {
    setMappings((prev) => prev.map((m) => (m.map_id === mapId
      ? { ...m, [field === 'account' ? 'account_code' : 'cost_center_code']: code }
      : m)));
    setDirty((prev) => ({ ...prev, [mapId]: true }));
    setPicker(null);
  }, []);

  const onHeaderMenu = useCallback((colIndex, bounds) => {
    const colId = colIds[colIndex];
    const acc = accessors[colId];
    if (!acc) return;
    const options = [...new Set(mappingsRef.current.map((m) => String(acc(m) ?? '')))].sort();
    setFilterUI({ colId, options, x: bounds ? bounds.x : 200, y: bounds ? bounds.y + bounds.height : 200 });
  }, [colIds, accessors]);

  // ---- Inline add line ----------------------------------------------------------
  const setE = (k, v) => setEntry((prev) => ({ ...prev, [k]: v }));

  const isDuplicate = entry.site_code && entry.job_title
    && mappings.some((m) => m.site_code === entry.site_code && m.job_title === entry.job_title);

  const entryProblem =
    !entry.site_code ? 'Pick a site.'
    : !entry.job_title ? 'Pick a job title.'
    : isDuplicate ? 'That site + job title is already mapped.'
    : !entry.account_code ? 'Pick a GL account.'
    : '';

  const addMapping = () => {
    if (entryProblem) return;
    const id = newId();
    setMappings((prev) => [...prev, {
      map_id: id, site_code: entry.site_code, job_title: entry.job_title,
      account_code: entry.account_code, cost_center_code: entry.cost_center_code || '', isNew: true,
    }]);
    setDirty((prev) => ({ ...prev, [id]: true }));
    setEntry(BLANK_ENTRY);
  };

  // ---- Save ----------------------------------------------------------------------
  const hasChanges = Object.keys(dirty).length > 0;

  const handleSave = useCallback(async () => {
    setError(null);
    const payload = mappingsRef.current.filter((m) => dirty[m.map_id]).map((m) => ({
      map_id: m.map_id,
      site_code: m.site_code,
      job_title: m.job_title,
      account_code: m.account_code,
      cost_center_code: m.cost_center_code || '',
    }));
    if (payload.length === 0) return;
    setIsSaving(true);
    try {
      const res = await peopleService.saveGlMap(payload);
      if (!res.success) throw new Error(res.message);
      await loadData();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save GL mapping.');
    } finally { setIsSaving(false); }
  }, [dirty, loadData]);

  const gridToolbar = useMemo(() => (
    <>
      {activeIds.length > 0 && (
        <button
          type="button"
          onClick={clearAll}
          className="px-3 py-1 rounded-md border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"
        >
          Clear filters ({activeIds.length})
        </button>
      )}
      <button
        type="button"
        onClick={handleSave}
        disabled={isSaving || !hasChanges}
        className={`px-3 py-1 rounded-md border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
          hasChanges
            ? 'border-emerald-700 bg-emerald-600 text-white font-semibold hover:bg-emerald-700'
            : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
        }`}
      >
        {isSaving ? 'Saving…' : 'Save'}
      </button>
    </>
  ), [handleSave, isSaving, hasChanges, activeIds.length, clearAll]);

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-4">
        <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">GL Mapping</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Map each Site + Job Title to a GL account and cost centre so people costs roll up into the GL view.
          One mapping per combination, per budget version. Click a GL Account or Cost Centre cell to change it.
        </p>
        {error && <div className="mt-3 p-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">{error}</div>}
      </div>

      {/* Inline add line */}
      {ready && (
        <div className="shrink-0 px-6 pb-3">
          <div className="flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3">
            {/* Type-to-search: a chart of accounts is far too long to scroll,
                and searching matches the code as well as the description. */}
            <div className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              <span>Site <span className="text-red-600 dark:text-red-400">*</span></span>
              <SearchSelect className="w-40" value={entry.site_code} onChange={(v) => setE('site_code', v)}
                options={siteOptions} placeholder="Select site…" emptyText="No site matches." />
            </div>
            <div className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              <span>Job title <span className="text-red-600 dark:text-red-400">*</span></span>
              <SearchSelect className="w-48" value={entry.job_title} onChange={(v) => setE('job_title', v)}
                options={titleOptions} placeholder="Select job title…" emptyText="No job title matches." />
            </div>
            <div className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              <span>GL account <span className="text-red-600 dark:text-red-400">*</span></span>
              <SearchSelect className="w-64" value={entry.account_code} onChange={(v) => setE('account_code', v)}
                options={accountOptions} placeholder="Select account…" emptyText="No account matches." />
            </div>
            <div className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              <span>Cost centre</span>
              <SearchSelect className="w-56" value={entry.cost_center_code} onChange={(v) => setE('cost_center_code', v)}
                options={ccOptions} placeholder="(None)" clearLabel="(None)" emptyText="No cost centre matches." />
            </div>
            <button
              type="button"
              onClick={addMapping}
              disabled={!!entryProblem}
              title={entryProblem || 'Add mapping'}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white mb-0.5 ${entryProblem ? 'bg-slate-400 dark:bg-slate-600 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}
            >
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
          {jobTitles.length === 0 && (
            <p className="mt-1.5 text-xs text-amber-600">No job titles yet — add them in People → Job Titles first.</p>
          )}
        </div>
      )}

      <div className="flex-1 min-h-0 px-6 pb-6">
        <div className="max-h-full flex flex-col rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
          {!ready ? (
            <div className="p-10 text-center text-sm text-slate-400">Select a budget version in the top bar to begin.</div>
          ) : isLoading ? (
            <div className="p-4 text-xs text-slate-400">Loading…</div>
          ) : mappings.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No mappings yet — add one on the line above.</div>
          ) : (
            <MantleDataGrid
              gridId="people-gl-map"
              fitHeight
              columns={gcols}
              rowCount={filtered.length}
              getCellContent={getCellContent}
              onCellClicked={onCellClicked}
              onHeaderMenuClick={onHeaderMenu}
              freezeColumns={2}
              hint="Click a GL Account or Cost Centre cell to change it. Header ▾ filters. ✕ removes a mapping."
              toolbar={gridToolbar}
            />
          )}
        </div>
      </div>

      {/* Column filter popover */}
      {filterUI && (
        <FilterPopover
          key={filterUI.colId}
          anchor={filterUI}
          options={filterUI.options}
          filter={filters[filterUI.colId]}
          onApply={(f) => setFilter(filterUI.colId, f)}
          onClose={() => setFilterUI(null)}
        />
      )}

      {/* GL account / cost centre picker */}
      {picker && (
        <PickerPopover
          key={`${picker.id}-${picker.field}`}
          anchor={picker}
          options={picker.field === 'account' ? accounts : [{ code: '', description: '(None)' }, ...costCenters]}
          onPick={(code) => applyPick(picker.id, picker.field, code)}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
};

export default GlMapping;
